import { Handshake } from "@phosphor-icons/react"
import { ErrorBoundary } from "@sentry/nextjs"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import NicerBlockquote from "../../../common/NicerBlockquote"
import DataPipelineIllustration from "../../../getListed/chainListing/DataPipelineIllustration"
import { MethodologyCard } from "../../../products/explorer/MethodologyCard"
import { ProjectMetricsSection } from "../../../products/explorer/ProjectMetricsSection"
import ProductPageHeader from "../../../products/ProductPageHeader"
import ProductPageTemplate from "../../../products/ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  ProductCTASection,
  StatsGrid,
  ThreeFeaturesSection,
} from "../../../products/shared"

const DataPartnershipsPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Handshake}
          title="Data Partnerships"
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/products/data-partnerships/banner.png`}
          subtitle="As part of a Data Partnership, Token  Terminal extracts, decodes, and maintains your blockchain's or application's onchain data to free your team to focus on building."
          ctaButtons={
            <LinkButton size="large" href="/contact" style={{ zIndex: 4 }}>
              Become a partner
            </LinkButton>
          }
          whoWeServeText="Blockchain and application developers who need accurate, standardized data without building infrastructure in-house."
          simpleWhoWeServeText="blockchain and application developers"
        />
      }
      productId="data-partnerships"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip
        logos={productPageLogos.dataPartnerships}
        title="Working directly with leading chains and apps"
      />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard className="p-0 h-auto">
                <div style={{ aspectRatio: "914 / 389", width: "100%" }}>
                  <DataPipelineIllustration />
                </div>
              </FeatureCard>
            ),
            title: (
              <>
                End-to-end data pipeline,
                <br />
                with zero overhead
              </>
            ),
            description:
              "We handle extraction, decoding, and standardization from genesis to real-time. No nodes to run, no pipelines to maintain.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <ErrorBoundary fallback={<div>Error loading dashboard</div>}>
                  <ProjectMetricsSection className="!mt-0 !mb-0" />
                </ErrorBoundary>
              </FeatureCard>
            ),
            title: "Standardized metrics to power comparable analysis",
            description:
              "Your project's key financial and usage metrics are presented alongside [ ]+ projects using the same methodology investors trust.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <MethodologyCard />
              </FeatureCard>
            ),
            title: "Rich dataset, with world-class visualization tools",
            description:
              "Query your and relevant peer group's decoded data via SQL, export to your warehouse, or access the data through our API. Your data, your way.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="Even though the industry is open source and most of the information is publicly available on the blockchain, it can still be a lot of work to get accurate and actionable data. We've found that Token Terminal is consistently putting out high quality data, with everything clearly documented."
          author="Felipe Montealegre"
          authorTitle="Theia"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-TheiaResearch.jpg`}
        />
      </div>

      {/* CTA Section */}
      <ProductCTASection
        tiers={[
          {
            id: "chains",
            title: "For Chains",
            features: [
              "Direct collaboration on data accuracy",
              "Priority decoding and coverage",
              "Distribution to 250+ million users",
              "Institutional visibility",
            ],
            cta: (
              <LinkButton className="w-full mt-4" size="large" href="/contact">
                Partner with us
              </LinkButton>
            ),
          },
          {
            id: "dapps",
            title: "For dApps",
            features: [
              "Validated revenue attribution",
              "Custom protocol-specific metrics",
              "Research coverage and visibility",
              "Data quality assurance",
            ],
            cta: (
              <LinkButton className="w-full mt-4" size="large" href="/contact" color="success">
                Get listed
              </LinkButton>
            ),
          },
        ]}
      />
    </ProductPageTemplate>
  )
}

export default DataPartnershipsPage
